import emailjs from '@emailjs/browser';
import { EMAILJS_CONFIG } from '../config/emailjs';

// Initialize EmailJS
emailjs.init(EMAILJS_CONFIG.publicKey);

// Format suggestion type for display
const formatSuggestionType = (type) => {
  const types = {
    'feature': '✨ Nova Funcionalidade',
    'improvement': '🔧 Melhoria',
    'bug': '🐛 Relatório de Bug',
    'brand': '🎨 Nova Marca',
    'ui': '🎨 Feedback UI/UX',
    'other': '💬 Outros'
  };
  return types[type] || type;
};

// Format priority for display
const formatPriority = (priority) => {
  const priorities = {
    'low': '🟢 Baixa Prioridade',
    'medium': '🟡 Média Prioridade',
    'high': '🔴 Alta Prioridade'
  };
  return priorities[priority] || priority;
};

const buildSuggestionParams = (formData) => {
  const formattedType = formatSuggestionType(formData.suggestionType);
  const formattedPriority = formatPriority(formData.priority);
  const submissionDate = new Date().toLocaleString('pt-PT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  const safeBrand = formData.brand || 'N/A';

  // Provide multiple key variants to match EmailJS template variables.
  return {
    name: formData.name,
    email: formData.email,
    subject: formData.subject,
    message: formData.message,
    suggestionType: formattedType,
    suggestion_type: formattedType,
    suggestionTypeRaw: formData.suggestionType,
    suggestion_type_raw: formData.suggestionType,
    priority: formattedPriority,
    priority_level: formattedPriority,
    priorityRaw: formData.priority,
    priority_raw: formData.priority,
    brand: safeBrand,
    brand_name: safeBrand,
    submissionDate: submissionDate,
    submission_date: submissionDate,
    reply_to: formData.email
  };
};

// Send suggestion email
export const sendSuggestionEmail = async (formData) => {
  try {
    const baseParams = buildSuggestionParams(formData);

    // Prepare email data for admin
    const emailData = {
      ...baseParams,
      to_name: 'MarkerSync Team',
      to_email: 'markersync@gmail.com',
      from_name: formData.name,
      from_email: formData.email
    };

    console.log('📧 Enviando email para admin com template:', EMAILJS_CONFIG.adminTemplateId);
    console.log('📧 Dados admin:', emailData);

    // Send email to admin team
    const adminResult = await emailjs.send(
      EMAILJS_CONFIG.serviceId,
      EMAILJS_CONFIG.adminTemplateId,
      emailData
    );

    console.log('✅ Admin email enviado:', adminResult);

    // Send confirmation email to user - simplified data structure
    const userEmailData = {
      ...baseParams,
      // Campos adicionais para compatibilidade
      to_name: formData.name,
      to_email: formData.email,
      user_name: formData.name,
      user_email: formData.email
    };

    console.log('📧 Enviando email de confirmação com template:', EMAILJS_CONFIG.confirmationTemplateId);
    console.log('📧 Dados confirmação:', userEmailData);

    // Tentar enviar email de confirmação
    let confirmationResult = null;
    let confirmationError = null;
    
    try {
      confirmationResult = await emailjs.send(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.confirmationTemplateId,
        userEmailData
      );
      console.log('✅ Confirmation email enviado:', confirmationResult);
    } catch (confirmError) {
      console.error('❌ Erro no email de confirmação:', confirmError);
      confirmationError = confirmError.message;
    }

    if (adminResult.status === 200) {
      return { 
        success: true, 
        message: confirmationResult?.status === 200 
          ? 'Sugestão enviada com sucesso! Receberá um email de confirmação.'
          : 'Sugestão enviada com sucesso! (Erro no email de confirmação)',
        confirmationSent: confirmationResult?.status === 200,
        confirmationError: confirmationError,
        adminTemplateUsed: EMAILJS_CONFIG.adminTemplateId,
        confirmationTemplateUsed: EMAILJS_CONFIG.confirmationTemplateId
      };
    } else {
      throw new Error('Failed to send admin email');
    }
  } catch (error) {
    console.error('❌ EmailJS Error:', error);
    console.error('🔧 Configuração atual:', EMAILJS_CONFIG);
    return { 
      success: false, 
      message: 'Erro ao enviar sugestão. Tente novamente ou contacte-nos diretamente.',
      error: error.message,
      templates: {
        admin: EMAILJS_CONFIG.adminTemplateId,
        confirmation: EMAILJS_CONFIG.confirmationTemplateId
      }
    };
  }
};

// Send publisher application email
export const sendPublisherApplicationEmail = async ({ name, email, message, uid }) => {
  try {
    const submissionDate = new Date().toLocaleString('pt-PT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });

    const emailData = {
      name: name || 'Anonymous',
      email,
      subject: '🎨 Publisher Application — MarkerSync Gallery',
      message: message
        ? `${message}\n\n---\nUID: ${uid || 'not logged in'}`
        : `(No message provided)\n\n---\nUID: ${uid || 'not logged in'}`,
      suggestionType: '🎨 Publisher Application',
      suggestion_type: '🎨 Publisher Application',
      suggestionTypeRaw: 'publisher_application',
      suggestion_type_raw: 'publisher_application',
      priority: '🟡 Média Prioridade',
      priority_level: '🟡 Média Prioridade',
      priorityRaw: 'medium',
      priority_raw: 'medium',
      brand: 'N/A',
      brand_name: 'N/A',
      submissionDate,
      submission_date: submissionDate,
      reply_to: email,
      to_name: 'MarkerSync Team',
      to_email: 'markersync@gmail.com',
      from_name: name || 'Anonymous',
      from_email: email,
    };

    const result = await emailjs.send(
      EMAILJS_CONFIG.serviceId,
      EMAILJS_CONFIG.adminTemplateId,
      emailData
    );

    return result.status === 200
      ? { success: true }
      : { success: false, message: 'Failed to send application.' };
  } catch (error) {
    console.error('❌ Publisher application email error:', error);
    return { success: false, message: error.message };
  }
};

// Test confirmation email template
export const testConfirmationEmail = async (testData = {}) => {
  const fakeData = {
    name: testData.name || 'Teste Usuario',
    email: testData.email || 'teste@exemplo.com',
    subject: testData.subject || 'Teste de confirmação',
    message: testData.message || 'Mensagem de teste para confirmar o template.',
    suggestionType: testData.suggestionType || 'feature',
    priority: testData.priority || 'medium',
    brand: testData.brand || ''
  };
  const baseParams = buildSuggestionParams(fakeData);
  const testEmailData = {
    ...baseParams,
    to_name: fakeData.name,
    to_email: fakeData.email,
    user_name: fakeData.name,
    user_email: fakeData.email
  };

  console.log('🧪 Testando template de confirmação:', EMAILJS_CONFIG.confirmationTemplateId);
  console.log('🧪 Dados de teste:', testEmailData);

  try {
    const result = await emailjs.send(
      EMAILJS_CONFIG.serviceId,
      EMAILJS_CONFIG.confirmationTemplateId,
      testEmailData
    );
    console.log('✅ Teste enviado com sucesso:', result);
    return { success: true, result };
  } catch (error) {
    console.error('❌ Erro no teste:', error);
    return { success: false, error: error.message };
  }
};

// Validate EmailJS configuration
export const validateEmailJSConfig = () => {
  const { publicKey, serviceId, adminTemplateId, confirmationTemplateId } = EMAILJS_CONFIG;
  
  if (publicKey === 'YOUR_PUBLIC_KEY' || 
      serviceId === 'YOUR_SERVICE_ID' || 
      adminTemplateId === 'YOUR_TEMPLATE_ID' ||
      confirmationTemplateId === 'YOUR_CONFIRMATION_TEMPLATE_ID') {
    console.warn('Erro! Configuração do EmailJS incompleta.');
    return false;
  }
  
  return true;
};
